<script lang="ts" setup>
import { ref, computed, watch } from "vue"
import { useTransition, TransitionPresets } from "@vueuse/core"
import { useMotion } from "@vueuse/motion"
import { useSpring } from "@/composables/use-spring"
import { useMenuGeometry } from "@/composables/use-menu-geometry"
import SVG_Letterform from "@/components/SVG_Letterform.vue"
import { letterforms } from "@/data/letterforms"
import MenuContainer from "./MenuContainer.vue"
import MenuNav from "./MenuNav.vue"
import Projects from "@/views/Projects.vue"
import Programmes from "@/views/Programmes.vue"
import Search from "@/views/Search.vue"
import { useMenuMachine } from "@/composables/use-menu-machine"
import {
  useStudentPageTransitions,
  StudentPageState,
} from "@/composables/use-student-page-transitions"
import SearchModal from "../SearchModal/SearchModal.vue"

const { state, activeView, navActive, contentActive, motionVariant, send } =
  useMenuMachine()

// Hide the menu while the student-page zoom (or its flight) is active. pageMode
// is a module-level singleton, so this tracks StudentZoom without any new store.
const { pageMode } = useStudentPageTransitions()
const zoomActive = computed(
  () =>
    pageMode.value === StudentPageState.ZOOM ||
    pageMode.value === StudentPageState.ZOOMFLGHT,
)

// ─── Letterform animation ───────────────────────────────────────────────────
// The M letterform height tracks the menu open state.

const letterformMRef = ref<HTMLElement>()
// The toggle overlay swallows the letterforms' pointer events, so the C/M use
// `global` mode — listening on window and masking to each letter's own box — so
// the pull tracks the cursor per-letter regardless of what sits on top.
const { config } = useSpring("snappy")
const geometry = useMenuGeometry()

const { apply } = useMotion(letterformMRef, {
  initial: { height: geometry.letterform.closedHeight() },
})

const letterformHeightFor = (variant: string): number => {
  switch (variant) {
    case "hover":
      return geometry.letterform.hoverHeight()
    case "open-small":
    case "open-large":
      return geometry.letterform.openHeight.value
    default:
      return geometry.letterform.closedHeight()
  }
}

watch(
  motionVariant,
  val => apply({ height: letterformHeightFor(val), transition: config }),
  { immediate: true },
)

// Re-fit the M when the viewport switches between mobile and desktop profile.
watch(geometry.isMobile, () =>
  apply({
    height: letterformHeightFor(motionVariant.value),
    transition: config,
  }),
)

// ─── Letter gap scale ───────────────────────────────────────────────────────
// Widens the gaps inside the M letterform when the menu is open.

const targetMinGapScale = computed(() => {
  if (state.value === "content-open") return window.innerWidth < 640 ? 1 : 3
  if (state.value === "nav-open") return window.innerWidth < 640 ? 1 : 2
  return window.innerWidth < 640 ? 0.75 : 1
})
const minGapScale = useTransition(targetMinGapScale, {
  duration: 200,
  transition: TransitionPresets.easeInOutCubic,
})

const headerButtonStyle = computed(() => {
  const padding =
    state.value === "closed" || state.value === "hovering"
      ? geometry.buttonPadding.collapsed
      : state.value === "content-open"
        ? geometry.buttonPadding.openLarge
        : geometry.buttonPadding.open

  return {
    paddingLeft: `${padding.x}px`,
    paddingRight: `${padding.x}px`,
    paddingTop: `${padding.top}px`,
    paddingBottom: `${padding.bottom}px`,
  }
})
</script>

<template>
  <MenuContainer
    :hidden="zoomActive"
    :state="state"
    @hover-enter="send('HOVER_ENTER')"
    @hover-leave="send('HOVER_LEAVE')"
    @close="send('CLOSE')"
    @toggle="send('TOGGLE')"
  >
    <template #header>
      <button
        class="w-full block cursor-pointer top-0 transition-[padding] duration-150 sticky z-30 leading-none group"
        :style="headerButtonStyle"
        @click="send('TOGGLE')"
      >
        <div
          ref="letterformMRef"
          class="w-full relative flex md:hover:fill-gold"
          :style="{ gap: minGapScale * 4 + 'px' }"
          :class="{
            'fill-black ': state === 'closed' || state === 'hovering',
            'fill-gold': state === 'hovering',
          }"
        >
          <SVG_Letterform
            :masters="letterforms.c"
            :stroke-width="1"
            :min-gap-scale="minGapScale"
            global
            stroke="black"
            class="transition-colors w-full"
          />

          <SVG_Letterform
            :masters="letterforms.m"
            :stroke-width="1"
            :min-gap-scale="minGapScale"
            global
            stroke="black"
            class="transition-colors w-full"
          />
          <div
            class="absolute inset-0 flex items-center justify-center border bg-white transition-all duration-150 text-sm tracking-tight leading-0 pointer-events-none opacity-0 group-hover:opacity-100"
            :class="{
              'opacity-100 border-transparent': state === 'hovering',
            }"
          >
            <div v-if="state === 'closed' || state === 'hovering'">Menu</div>
            <div v-if="state === 'nav-open'">Close</div>
            <div v-if="state === 'content-open'">Back</div>
          </div>
        </div>
      </button>
    </template>

    <MenuNav
      v-if="state === 'nav-open'"
      :nav-active="navActive"
      @select="view => send({ type: 'SELECT', view })"
      @close="send('CLOSE')"
    />

    <Transition name="menu-content">
      <div v-if="state === 'content-open' && contentActive" class="h-full">
        <!--
          Suspense provides the async boundary for Projects / Programmes.
          Transition sits inside Suspense so Vue fires the enter animation
          when the async component resolves (first load) AND when the key
          changes (view switch). The fallback prevents an empty content area
          during the initial network fetch.
        -->
        <Suspense>
          <Transition name="fade-slide" :key="String(activeView)">
            <Programmes v-if="activeView === 'programmes'" />
            <Projects v-else-if="activeView === 'projects'" />
            <Search v-else-if="activeView === 'search'" />
          </Transition>
          <template #fallback>
            <div class="px-4 py-4 opacity-40 text-sm">Loading…</div>
          </template>
        </Suspense>
      </div>
    </Transition>
  </MenuContainer>
</template>

<style scoped>
/* Outer wrapper: fades + slides up when the content panel appears */
.menu-content-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.menu-content-leave-active {
  transition: opacity 0.2s ease;
}
.menu-content-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.menu-content-leave-to {
  opacity: 0;
}

/* Inner panel: fires on Suspense resolve (first load) and on view switch */
.fade-slide-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.fade-slide-leave-active {
  transition: opacity 0.2s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-leave-to {
  opacity: 0;
}
</style>
